/**
 * Operator surface — the press-side inspection workspace, and the heart of
 * spec 2.8: onboard camera + lighting → guided baseline learning → shadow →
 * active advisory inspection.
 *
 * Built around two principles from the audit:
 *  1. Baseline learning is unsupervised-first: the operator photographs
 *     known-good parts; defect samples are never required.
 *  2. The advisory limitation stays on screen during live inspection.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, qs } from '../api/client';
import { useApi, useEventStream, ago } from '../hooks/useApi';
import type {
  Station,
  CaptureResult,
  InspectionEvent,
  TransitionResult,
  Me,
} from '../api/types';
import { Card, Empty, ErrorBox, Loading, StatePill, Tag } from '../components/Metric';
import { AdvisoryBanner } from '../components/Advisory';
import { RoiEditor, type Roi } from '../components/RoiEditor';

const MIN_BASELINE = 20;
const RECOMMENDED_MAX = 50;

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

async function grabFrame(video: HTMLVideoElement): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 1280;
  canvas.height = video.videoHeight || 960;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable in this browser.');
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}

function Steps({ station }: { station: Station }) {
  const steps = [
    { label: 'Camera onboarded', done: true },
    { label: `Baseline (${station.baselineImageCount}/${MIN_BASELINE})`, done: station.baselineReady },
    { label: 'Shadow mode', done: station.state === 'SHADOW' || station.state === 'ACTIVE' },
    { label: 'Active inspection', done: station.state === 'ACTIVE' },
  ];
  const currentIdx = steps.findIndex((s) => !s.done);
  return (
    <div className="steps" aria-label="Onboarding progress">
      {steps.map((s, i) => (
        <div key={s.label} className={`step ${s.done ? 'done' : i === currentIdx ? 'current' : ''}`}>
          <span className="n">{s.done ? '✓' : i + 1}</span>
          {s.label}
        </div>
      ))}
    </div>
  );
}

/** Human-readable rendering of the live event stream (no raw JSON by default). */
function EventRow({ m }: { m: { type: string; payload: unknown; at: string } }) {
  const p = (m.payload ?? {}) as Record<string, unknown>;
  let summary = '';
  let tone = 'muted';
  if (m.type === 'inspection_event') {
    const outcome = String(p.outcome ?? '');
    const defect = p.defectName ? ` — ${p.defectName}` : '';
    const conf = typeof p.confidence === 'number' ? ` · ${(p.confidence * 100).toFixed(0)}%` : '';
    const lat = typeof p.latencyMs === 'number' ? ` · ${p.latencyMs} ms` : '';
    summary = `${outcome.replace(/_/g, ' ')}${defect}${conf}${lat}`;
    tone = outcome === 'PASS' ? 'ok' : outcome === 'INSUFFICIENT_DATA' ? 'muted' : 'advisory';
  } else if (m.type === 'station_state') {
    summary = `Station state → ${String(p.state ?? p.to ?? '')}`;
    tone = 'info';
  } else if (m.type === 'baseline_update') {
    summary = `Baseline updated (${String(p.count ?? '')} references)`;
    tone = 'info';
  } else if (m.type === 'alert') {
    summary = String(p.message ?? 'Alert raised');
    tone = 'advisory';
  } else if (m.type === 'subscription_update') {
    summary = `Subscription → ${String(p.status ?? '')}`;
    tone = 'warn';
  } else {
    summary = m.type;
  }
  return (
    <tr>
      <td className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
        {m.at}
      </td>
      <td>
        <span className={`pill ${tone}`}>{m.type.replace(/_/g, ' ')}</span>
      </td>
      <td style={{ fontSize: 12.5 }}>{summary}</td>
      <td style={{ textAlign: 'right' }}>
        <details className="raw">
          <summary>Raw</summary>
          <pre>{JSON.stringify(m.payload, null, 2)}</pre>
        </details>
      </td>
    </tr>
  );
}

export function StationPage({ me }: { me: Me }) {
  const navigate = useNavigate();
  const { data: stations, error, loading, reload } = useApi<Station[]>(
    () => api.get<Station[]>(`/api/v1/stations${qs({ limit: 50 })}`),
    [],
  );
  const [stationId, setStationId] = useState<string>('');
  const station = useMemo(
    () => stations?.find((s) => s.id === stationId) ?? stations?.[0] ?? null,
    [stations, stationId],
  );

  const detail = useApi<Station>(
    () =>
      station?.id
        ? api.get<Station>(`/api/v1/stations/${station.id}`)
        : Promise.reject(new Error('No station selected yet')),
    [station?.id],
  );
  const active: Station | null = detail.data ?? station;

  const { messages, connected } = useEventStream(30);
  const [last, setLast] = useState<CaptureResult | null>(null);
  const [lastImage, setLastImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [baselineBuffer, setBaselineBuffer] = useState<string[]>([]);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    setFail(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 960 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setCameraOn(true);
    } catch (err) {
      setFail(
        `Camera unavailable: ${(err as Error).message}. ` +
          'You can still upload image files below — the processing is identical.',
      );
    }
  }, []);

  useEffect(() => stopCamera, [stopCamera]);

  useEffect(() => {
    const latest = messages.find((m) => m.type === 'inspection_event');
    if (latest) setLast(latest.payload as CaptureResult);
  }, [messages]);

  async function submitCapture(dataUrl: string, cycleId: string) {
    if (!active) return;
    setBusy(true);
    setFail(null);
    setNotice(null);
    try {
      const result = await api.post<CaptureResult>(
        `/api/v1/stations/${active.id}/captures`,
        { dataUrl, cycleId },
      );
      setLast(result);
      setLastImage(dataUrl);
      detail.reload();
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function captureNow() {
    if (!videoRef.current || !cameraOn) return;
    const dataUrl = await grabFrame(videoRef.current);
    await submitCapture(dataUrl, `cycle-${Date.now()}`);
  }

  async function uploadCapture(files: FileList | null) {
    if (!files || files.length === 0) return;
    const dataUrl = await fileToDataUrl(files[0]!);
    await submitCapture(dataUrl, `cycle-${Date.now()}`);
  }

  async function collectBaselineFrames(count: number) {
    if (!cameraOn || !videoRef.current) return;
    const frames: string[] = [];
    for (let i = 0; i < count; i++) {
      frames.push(await grabFrame(videoRef.current));
      await new Promise((r) => setTimeout(r, 160));
    }
    setBaselineBuffer((prev) => [...prev, ...frames]);
  }

  async function uploadBaseline(files: FileList | null) {
    if (!files) return;
    const urls: string[] = [];
    for (const f of Array.from(files)) urls.push(await fileToDataUrl(f));
    setBaselineBuffer((prev) => [...prev, ...urls]);
  }

  async function commitBaseline() {
    if (!active || baselineBuffer.length === 0) return;
    setBusy(true);
    setFail(null);
    try {
      await api.post(`/api/v1/stations/${active.id}/baseline`, {
        imageDataUrls: baselineBuffer,
        roi: active.roiJson ?? undefined,
      });
      setBaselineBuffer([]);
      setNotice(`Added ${baselineBuffer.length} baseline references.`);
      detail.reload();
      reload();
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function transition(to: 'SHADOW' | 'ACTIVE' | 'PASSIVE') {
    if (!active) return;
    setBusy(true);
    setFail(null);
    setNotice(null);
    try {
      const result = await api.post<TransitionResult>(
        `/api/v1/stations/${active.id}/transition`,
        { to },
      );
      if (!result.check.allowed) {
        setFail(result.check.reason);
      } else if (result.check.warnings.length > 0) {
        setNotice(result.check.warnings.join(' '));
      } else {
        setNotice(`Station is now ${result.station.state}.`);
      }
      detail.reload();
      reload();
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function override(eventId: string, to: 'good' | 'scrap') {
    setFail(null);
    try {
      await api.post(`/api/v1/events/${eventId}/override`, {
        overrideTo: to,
        note: 'Operator correction at the press',
      });
      detail.reload();
    } catch (err) {
      setFail((err as Error).message);
    }
  }

  async function saveRoi(roi: Roi | null) {
    if (!active) return;
    setFail(null);
    try {
      await api.patch(`/api/v1/stations/${active.id}`, { roi });
      setNotice(roi ? 'Inspection region saved.' : 'Inspection region cleared.');
      detail.reload();
    } catch (err) {
      setFail((err as Error).message);
    }
  }

  if (loading) return <Loading label="Loading stations…" />;
  if (error) return <ErrorBox message={error} />;
  if (!stations || stations.length === 0) {
    return (
      <Empty title="No camera stations yet">
        An organisation administrator needs to register a site, a machine and a
        camera station before inspection can begin.
      </Empty>
    );
  }

  const baseline = active?.baseline;
  const canTransitionToShadow =
    active?.state === 'PASSIVE' && (baseline?.ready ?? active?.baselineReady ?? false);
  const roiImage = lastImage ?? (last?.mediaKeys?.[0] ? `/api/v1/media/${encodeURIComponent(last.mediaKeys[0]!)}` : null);

  return (
    <>
      <div className="btn-row" style={{ marginBottom: 16 }}>
        <label htmlFor="station-select" style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600 }}>
          Station
        </label>
        <select
          id="station-select"
          style={{ maxWidth: 340 }}
          value={active?.id ?? ''}
          onChange={(e) => setStationId(e.target.value)}
        >
          {stations.map((s) => (
            <option key={s.id} value={s.id}>
              {s.machine?.site?.name} · {s.machine?.name} · {s.name}
            </option>
          ))}
        </select>
        {active && <StatePill state={active.state} />}
        <Tag>{active?.activeRecipe?.name ?? 'No recipe bound'}</Tag>
        <div style={{ flex: 1 }} />
        <button className="btn sm" onClick={() => navigate('/station/kiosk')}>
          Kiosk mode
        </button>
      </div>

      {fail && <div className="error-box">{fail}</div>}
      {notice && <div className="callout info">{notice}</div>}

      <Steps station={active!} />

      <div className="grid grid-2">
        {/* ── Camera + region ─────────────────────────────────────────── */}
        <Card
          title="Camera"
          actions={
            !cameraOn ? (
              <button className="btn sm primary" onClick={() => void startCamera()}>
                Start camera
              </button>
            ) : (
              <button className="btn sm" onClick={stopCamera}>
                Stop
              </button>
            )
          }
          note={
            <>
              Sensor <span className="mono">{active?.sensorType}</span> · lighting{' '}
              <span className="mono">{active?.lightingMode}</span>
              {active?.lightingNotes ? ` · ${active.lightingNotes}` : ''}
            </>
          }
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: '100%',
              borderRadius: 8,
              background: '#10161d',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              display: cameraOn ? 'block' : 'none',
            }}
          />
          {!cameraOn && !roiImage && (
            <div className="empty">
              <strong>Camera is off</strong>
              Start the camera to collect baseline frames and inspect cycles. You
              can also upload image files instead — the processing is identical.
            </div>
          )}
          <div className="btn-row" style={{ marginTop: 12 }}>
            <label className="btn sm">
              Upload image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  void uploadCapture(e.target.files);
                  const f = e.target.files?.[0];
                  if (f) void fileToDataUrl(f).then(setLastImage);
                }}
              />
            </label>
            <button
              className="btn primary"
              disabled={!cameraOn || busy || active?.state === 'PASSIVE'}
              onClick={() => void captureNow()}
            >
              {busy ? 'Processing…' : 'Inspect cycle'}
            </button>
            {active?.state === 'PASSIVE' && (
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                Inspection starts once the station leaves PASSIVE.
              </span>
            )}
          </div>

          {me.capabilities.includes('station:roi:draw') && roiImage && (
            <div style={{ marginTop: 16 }}>
              <div className="divider" style={{ margin: '14px 0' }} />
              <h3 style={{ marginBottom: 8 }}>Inspection region</h3>
              <RoiEditor
                imageSrc={roiImage}
                initial={(active?.roiJson as Roi | null) ?? null}
                onSave={saveRoi}
                busy={busy}
              />
            </div>
          )}
        </Card>

        {/* ── Latest verdict ──────────────────────────────────────────── */}
        <Card title="Latest result">
          {!last ? (
            <div className="empty">
              <strong>No cycle inspected yet</strong>
              Results appear here and over the live stream.
            </div>
          ) : (
            <>
              <div
                className={`press-state ${
                  last.outcome === 'PASS'
                    ? 'pass'
                    : last.outcome === 'INSUFFICIENT_DATA'
                      ? 'idle'
                      : 'flag'
                }`}
              >
                <div className="big">
                  {last.outcome === 'PASS'
                    ? 'PASS'
                    : last.outcome === 'INSUFFICIENT_DATA'
                      ? 'NEEDS REVIEW'
                      : last.outcome === 'REJECT_NON_SAFETY_GATE'
                        ? 'REJECT'
                        : 'CHECK PART'}
                </div>
                <div className="detail">
                  {last.defectName
                    ? `${last.defectName} · confidence ${(last.confidence * 100).toFixed(0)}%`
                    : last.sufficientEvidence
                      ? `Confidence ${(last.confidence * 100).toFixed(0)}%`
                      : 'Not enough evidence to decide'}
                </div>
                <div className="detail" style={{ fontSize: 12 }}>
                  Action taken: <span className="mono">{last.actionTaken}</span> · {last.latencyMs} ms
                </div>
              </div>

              {last.explanation && (
                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12 }}>
                  {last.explanation}
                </p>
              )}

              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>
                Grounded in:{' '}
                <span className="mono">{last.groundingSources.join(', ') || 'NONE'}</span>
                {' · '}
                {last.citedKbChunks.length} reference{last.citedKbChunks.length === 1 ? '' : 's'} cited
                {last.adapterKind === 'SIMULATION' && (
                  <>
                    {' · '}
                    <span className="pill warn">SIMULATION — not an AI model</span>
                  </>
                )}
              </div>

              {last.mediaKeys[0] && (
                <div style={{ marginTop: 10 }}>
                  <a href={`/api/v1/media/${encodeURIComponent(last.mediaKeys[0]!)}`}>
                    View original image
                  </a>
                </div>
              )}

              <div className="divider" style={{ margin: '14px 0' }} />
              <div className="btn-row">
                <span style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--ink-2)' }}>
                  Was the model right?
                </span>
                <button className="btn sm" onClick={() => void override(last.eventId, 'good')}>
                  Good part
                </button>
                <button className="btn sm danger" onClick={() => void override(last.eventId, 'scrap')}>
                  Scrap
                </button>
              </div>
              <div className="card-note">
                Your correction is stored alongside the model output, never over
                it. This is what makes the escape-rate and false-reject-rate
                figures real.
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        {/* ── Baseline learning ───────────────────────────────────────── */}
        <Card
          title="Guided baseline learning"
          actions={
            active?.baselineReady ? (
              <span className="pill ok">Baseline ready</span>
            ) : (
              <span className="pill warn">
                {active?.baselineImageCount ?? 0} / {MIN_BASELINE}
              </span>
            )
          }
          note={
            baseline?.message ??
            `Photograph ${MIN_BASELINE}–${RECOMMENDED_MAX} KNOWN-GOOD parts. Defect samples are not needed — this is unsupervised learning, which is why no vision engineer has to visit the line.`
          }
        >
          <div className={`bar${active?.baselineReady ? ' ok' : ''}`} style={{ marginBottom: 12 }}>
            <span
              style={{
                width: `${Math.min(100, ((active?.baselineImageCount ?? 0) / MIN_BASELINE) * 100)}%`,
              }}
            />
          </div>
          <div className="btn-row">
            <button className="btn sm" disabled={!cameraOn || busy} onClick={() => void collectBaselineFrames(5)}>
              Capture 5 good parts
            </button>
            <button className="btn sm" disabled={!cameraOn || busy} onClick={() => void collectBaselineFrames(10)}>
              Capture 10
            </button>
            <label className="btn sm">
              Upload good-part images
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => void uploadBaseline(e.target.files)}
              />
            </label>
            {baselineBuffer.length > 0 && (
              <>
                <span className="pill info">{baselineBuffer.length} staged</span>
                <button className="btn sm primary" disabled={busy} onClick={() => void commitBaseline()}>
                  Add to baseline
                </button>
                <button className="btn sm ghost" onClick={() => setBaselineBuffer([])}>
                  Discard
                </button>
              </>
            )}
          </div>
        </Card>

        {/* ── POV state control ───────────────────────────────────────── */}
        {me.capabilities.includes('station:pov:operate') ? (
          <Card
            title="Inspection mode"
            note="PASSIVE collects baseline only. SHADOW runs the model and logs results without acting. ACTIVE drives the advisory flag and the non-safety 24 V reject output. None of these can stop the machine."
          >
            <div className="btn-row">
              <button
                className="btn primary"
                disabled={!canTransitionToShadow || busy}
                onClick={() => void transition('SHADOW')}
              >
                Enter shadow mode
              </button>
              <button
                className="btn"
                disabled={active?.state !== 'SHADOW' || busy}
                onClick={() => void transition('ACTIVE')}
              >
                Go active
              </button>
              <button
                className="btn ghost"
                disabled={active?.state === 'PASSIVE' || busy}
                onClick={() => void transition('PASSIVE')}
              >
                Back to passive
              </button>
            </div>
            {!canTransitionToShadow && active?.state === 'PASSIVE' && (
              <div className="card-note" style={{ marginTop: 10 }}>
                Needs {MIN_BASELINE} baseline images and a bound recipe.
              </div>
            )}
          </Card>
        ) : (
          <Card title="Inspection mode">
            <Empty title="Managed by your organisation">
              Mode transitions (passive → shadow → active) are performed by roles
              with station operation permission.
            </Empty>
          </Card>
        )}
      </div>

      {/* ── Recent cycles ─────────────────────────────────────────────── */}
      <div style={{ marginTop: 14 }}>
        <Card title="Recent cycles">
          {!active?.recentEvents || active.recentEvents.length === 0 ? (
            <Empty title="No cycles yet" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Cycle</th>
                    <th>Result</th>
                    <th className="num">Confidence</th>
                    <th>Human label</th>
                    <th>Action</th>
                    <th>When</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {active.recentEvents.map((e: InspectionEvent) => (
                    <tr key={e.id}>
                      <td className="mono" style={{ fontSize: 11.5 }}>{e.cycleId}</td>
                      <td>
                        <StatePill state={e.outcome} />
                        {e.defectName && <div className="dim">{e.defectName}</div>}
                      </td>
                      <td className="num">{(e.confidence * 100).toFixed(0)}%</td>
                      <td>
                        {e.humanLabel ? (
                          <span className={`pill ${e.humanLabel === 'scrap' ? 'danger' : 'ok'}`}>
                            {e.humanLabel}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--ink-3)' }}>—</span>
                        )}
                        {e.operatorOverride && (
                          <div style={{ fontSize: 10.5, color: 'var(--advisory)', fontWeight: 650 }}>
                            corrected
                          </div>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>{e.actionTaken}</td>
                      <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>{ago(e.createdAt)}</td>
                      <td>
                        {e.mediaKeys?.[0] && (
                          <a className="btn sm" href={`/api/v1/media/${encodeURIComponent(e.mediaKeys[0]!)}`}>
                            Image
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* ── Live stream + safety context ──────────────────────────────── */}
      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <Card
          title="Live events"
          actions={
            <span className={`pill ${connected ? 'ok' : 'warn'}`}>
              {connected ? 'Connected' : 'Reconnecting'}
            </span>
          }
          note="Human-readable summary first; the raw payload is one click away for engineers."
        >
          {messages.length === 0 ? (
            <Empty title="Waiting for events">
              Inspection results and alerts appear here as they happen.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <tbody>
                  {messages.slice(0, 12).map((m, i) => (
                    <EventRow key={i} m={m} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="What this system can and cannot do">
          <AdvisoryBanner />
          <div className="card-note" style={{ marginTop: 0 }}>
            If you need a certified stop — a safety relay or safety PLC on the
            EUROMAP 67 dual-channel interlock — that must be designed and
            validated by a qualified safety integrator. PlasticVision may report
            data to it; it must not be the trigger of record.
          </div>
        </Card>
      </div>
    </>
  );
}
